import { api } from './api';
import { AxiosRequestConfig } from 'axios';
import { EventLog, EventListParams, EventListResponse, EventTraceResponse } from '../types';

const getWithFallback = async <T>(
  primaryPath: string,
  legacyPath: string | null,
  config?: AxiosRequestConfig
): Promise<T> => {
  try {
    const res = await api.get<T>(primaryPath, config);
    return res.data;
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (legacyPath && status === 404) {
      const legacyRes = await api.get<T>(legacyPath, config);
      return legacyRes.data;
    }
    throw err;
  }
};

const buildListParams = (params?: EventListParams) => {
  const searchParams = new URLSearchParams();
  if (!params) {
    return searchParams;
  }

  const {
    search,
    requestId,
    traceId,
    userId,
    event,
    category,
    outcome,
    reasonCode,
    route,
    method,
    from,
    to,
    limit,
    offset,
  } = params;

  const setIfTrimmed = (key: string, value?: string) => {
    const trimmed = value?.trim();
    if (trimmed) {
      searchParams.set(key, trimmed);
    }
  };

  setIfTrimmed('search', search);
  setIfTrimmed('requestId', requestId);
  setIfTrimmed('traceId', traceId);
  if (typeof userId === 'number') {
    searchParams.set('userId', String(userId).trim());
  } else if (typeof userId === 'string') {
    const trimmedUserId = userId.trim();
    if (trimmedUserId) {
      searchParams.set('userId', trimmedUserId);
    }
  }
  setIfTrimmed('event', event);
  setIfTrimmed('category', category);
  setIfTrimmed('outcome', outcome);
  setIfTrimmed('reasonCode', reasonCode);
  setIfTrimmed('route', route);
  setIfTrimmed('method', method);
  setIfTrimmed('from', from);
  setIfTrimmed('to', to);
  if (typeof limit === 'number') searchParams.set('limit', String(limit));
  if (typeof offset === 'number') searchParams.set('offset', String(offset));

  return searchParams;
};

export const fetchEvents = async (params?: EventListParams): Promise<EventListResponse> => {
  const searchParams = buildListParams(params);
  const payload = await getWithFallback<EventListResponse | EventLog[]>('/admin/events', '/admin/security-events', {
    params: searchParams,
  });
  if (Array.isArray(payload)) {
    return {
      data: payload,
      pagination: {
        total: payload.length,
        limit: params?.limit,
        offset: params?.offset,
      },
    };
  }
  return payload;
};

export const fetchEvent = async (eventId: number | string): Promise<EventLog> => {
  return getWithFallback<EventLog>(`/admin/events/${eventId}`, `/admin/security-events/${eventId}`);
};

export const fetchEventTrace = async (requestId: string): Promise<EventTraceResponse> => {
  const payload = await getWithFallback<EventTraceResponse | EventLog[]>(
    `/admin/events/trace/${requestId}`,
    `/admin/security-events/trace/${requestId}`
  );
  if (Array.isArray(payload)) {
    return { data: payload };
  }
  return payload;
};
